import React, { createContext, useContext, ReactNode } from 'react';
import { createClient } from '@supabase/supabase-js';

// ✅ Variáveis de ambiente — sempre usar URL direta do Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// ✅ Validação das variáveis de ambiente
if (!supabaseUrl) {
  throw new Error('VITE_SUPABASE_URL is not defined. Please check your environment variables.');
}

if (!supabaseAnonKey) {
  throw new Error('VITE_SUPABASE_ANON_KEY is not defined. Please check your environment variables.');
}

// ✅ Inicializa o cliente Supabase com configurações otimizadas
const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  global: {
    headers: {
      'Content-Type': 'application/json',
    },
  },
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

interface SupabaseContextProps {
  supabase: typeof supabase;
  invokeFunction: <T = any>(
    functionName: string,
    payload?: Record<string, any>
  ) => Promise<{
    data: T | null;
    error: Error | null;
  }>;
  testConnection: () => Promise<boolean>;
}

const SupabaseContext = createContext<SupabaseContextProps | undefined>(undefined);

// ✅ Provider correto
export const SupabaseProvider = ({ children }: { children: ReactNode }) => {
  // ✅ Função para testar a conexão
  const testConnection = async (): Promise<boolean> => {
    try {
      const { error } = await supabase.from('profiles').select('id').limit(1);

      if (error) {
        if (error.code === 'PGRST116' || error.message?.includes('row-level security')) {
          return true;
        }

        return false;
      }

      return true;
    } catch (err: any) {
      return false;
    }
  };

  // ✅ Função para chamar Edge Function
  const invokeFunction = async <T = any>(
    functionName: string,
    payload?: Record<string, any>
  ) => {
    try {
      console.log(`Invoking function: ${functionName}`, payload);

      const { data, error } = await supabase.functions.invoke<T>(functionName, {
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (error) {
        console.error(`Function ${functionName} error:`, error);
      } else {
        console.log(`Function ${functionName} success:`, data);
      }

      return { data, error };
    } catch (err: any) {
      console.error(`Error invoking function ${functionName}:`, err);

      return {
        data: null,
        error: new Error(err.message || 'Unknown error occurred'),
      };
    }
  };

  return (
    <SupabaseContext.Provider value={{ supabase, invokeFunction, testConnection }}>
      {children}
    </SupabaseContext.Provider>
  );
};

// ✅ Hook corrigido
export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (context === undefined) {
    throw new Error('useSupabase deve ser usado dentro de um SupabaseProvider');
  }
  return context;
};
